import { ContractEntityTooltipIdentifierEnum } from '@app/modules/contract/components/tooltip/contract-entity-tooltip/contract-entity-tooltip.model';
import {
	CONTRACT_BASE_METADATA,
	IContractBaseMetadata
}                                              from '@app/modules/contract/config/contract-base.config';
import {
	ContractLogPeriodEnum,
	ContractStatusEnum,
	ContractTypeGroupEnum
}                                              from '@app/modules/contract/models/contract.model';
import { APP_CONFIG }                          from '@appConfig/_app-config_master';
import { ITabContent }                         from '@core/interfaces/common.interfaces';
import {
	IFormBaseData,
	IFormCreateData,
	IFormUpdateData
}                                              from '@core/interfaces/form.interface';
import { SortOrderEnum }                       from '@shared/list-operations/components/sort-filter/sort-filter.model';
import { LIST_OPERATIONS_METADATA }            from '@shared/list-operations/config/list-operations.config';
import {
	FilterIdentifiersEnum,
	FilterTypes
}                                              from '@shared/list-operations/model/filter.model';
import {
	IListOperationsConfig,
	ListOperationsIdentifierEnum
}                                              from '@shared/list-operations/model/list-operations.model';
import { PagingModeEnum }                      from '@shared/list-operations/model/paging.model';

export interface IFiskalContractMetadata extends IContractBaseMetadata {
	entityId: string;

	entityTooltip: {
		allowedCommonIdentifiers: ContractEntityTooltipIdentifierEnum[];
		allowedFiskalIdentifiersTypeGroupsAGS: ContractEntityTooltipIdentifierEnum[];
		allowedFiskalIdentifiersTypeGroupV: ContractEntityTooltipIdentifierEnum[];
		allowedFiskalFccIdentifiers: ContractEntityTooltipIdentifierEnum[]
	};

	storeId: string;

	details: {
		dateFormat: Intl.DateTimeFormatOptions;
		internalTabContent: ITabContent[];
	};

	listOperations: IListOperationsConfig;

	i18n: {
		basepath: string;
		page: string;
		entity: string;
		form: string;
		details: string;
		tab: string;
	};

	form: {
		name: string;
		customerIdentifierValidationPattern: RegExp;
		projectNumberValidationPattern: RegExp;
		baseData: IFormBaseData;
		createData: IFormCreateData[];
		updateData: IFormUpdateData[];
		preserveData: IFormUpdateData[];
		preserveDataMyAccount: IFormUpdateData[];
	};
}

export const FISKAL_CONTRACT_METADATA: IFiskalContractMetadata = {
	...CONTRACT_BASE_METADATA,
	entityId: APP_CONFIG.fiskalContracts.entityId,
	entityTooltip: {
		allowedCommonIdentifiers: [ ...CONTRACT_BASE_METADATA.entityTooltip.allowedCommonIdentifiers ],
		allowedFiskalIdentifiersTypeGroupsAGS: [
			ContractEntityTooltipIdentifierEnum.FISKAL_CONTRACT_TYPE_GROUP,
			ContractEntityTooltipIdentifierEnum.FISKAL_CONTRACTED_CLOUD_ERS,
			ContractEntityTooltipIdentifierEnum.FISKAL_CONTRACTED_TOKEN_ERS
		],
		allowedFiskalIdentifiersTypeGroupV: [
			ContractEntityTooltipIdentifierEnum.FISKAL_CONTRACT_TYPE_GROUP,
			ContractEntityTooltipIdentifierEnum.FISKAL_CONTRACTED_BUSINESS_PREMISES,
			ContractEntityTooltipIdentifierEnum.FISKAL_CONTRACTED_BUSINESS_PREMISES_LIMIT,
			ContractEntityTooltipIdentifierEnum.FISKAL_VOLUME,
			ContractEntityTooltipIdentifierEnum.FISKAL_CONTRACTED_FCC_DATA_CENTER_VOLUME,
			ContractEntityTooltipIdentifierEnum.FISKAL_CONTRACTED_FCC_DATA_BUSINESS_PREMISSES
		],
		allowedFiskalFccIdentifiers: [
			ContractEntityTooltipIdentifierEnum.FISKAL_CONTRACTED_FCC_DATA_CENTER_ENABLED,
			ContractEntityTooltipIdentifierEnum.FISKAL_CONTRACTED_FCC_DATA_CENTER_ERS
		]
	},
	storeId: APP_CONFIG.fiskalContracts.store.id,
	listOperations: {
		id: ListOperationsIdentifierEnum.CONTRACTS_LIST_PAGE_FISKAL_TAB,
		pagingData: {
			mode: PagingModeEnum.Light,
			continuesPagesMode: false,
			defaultValues: {
				pageSize: LIST_OPERATIONS_METADATA.default.paging.pageSize ?? 20,
				pageNumber: LIST_OPERATIONS_METADATA.default.paging.pageNumber ?? 0
			}
		},
		searchData: {
			placeholderI18nPath: 'contract.search.placeholder'
		},
		sortData: [
			{ name: 'customerIdentifier', i18nPath: 'contract.sort.customerIdentifier.label' },
			{ name: 'customerName', i18nPath: 'contract.sort.customerName.label', defaultActive: true, sortOrder: SortOrderEnum.ASC }
		],
		filterData: [
			{
				order: 1,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.FILTER,
				type: FilterTypes.FILTER_TEXT_TYPE,
				i18nPath: 'contract.filter.queryFilter',
				items: [
					{ i18nPath: 'contract.filter.queryFilter' }
				]
			},
			{
				order: 2,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.PROJECT_NUMBER,
				type: FilterTypes.FILTER_TEXT_TYPE,
				i18nPath: 'contract.filter.projectNumber',
				items: [
					{ i18nPath: 'contract.filter.projectNumber' }
				]
			},
			{
				order: 3,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.CONTRACT_LOG_PERIOD,
				type: FilterTypes.FILTER_LIST_TYPE,
				i18nPath: 'contract.filter.contractLogPeriod',
				values: [ ContractLogPeriodEnum.CURRENT ],
				items: [
					{ value: ContractLogPeriodEnum.FUTURE, i18nPath: 'contract.filter.contractLogPeriod.periodItems.future' },
					{ value: ContractLogPeriodEnum.CURRENT, i18nPath: 'contract.filter.contractLogPeriod.periodItems.current' },
					{ value: ContractLogPeriodEnum.PAST, i18nPath: 'contract.filter.contractLogPeriod.periodItems.past' }
				]
			},
			{
				order: 4,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.CONTRACT_TYPE_GROUP,
				type: FilterTypes.FILTER_LIST_TYPE,
				i18nPath: 'contract.filter.contractTypeGroup',
				items: [
					{ value: ContractTypeGroupEnum.S, i18nPath: 'contract.form.contractTypeGroupItem.S' },
					{ value: ContractTypeGroupEnum.A, i18nPath: 'contract.form.contractTypeGroupItem.A' },
					{ value: ContractTypeGroupEnum.G, i18nPath: 'contract.form.contractTypeGroupItem.G' },
					{ value: ContractTypeGroupEnum.V, i18nPath: 'contract.form.contractTypeGroupItem.V' }
				]
			},
			{
				order: 8,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.FCC_DATACENTER_CONTRACT_TYPE_GROUP,
				type: FilterTypes.FILTER_LIST_TYPE,
				i18nPath: 'contract.filter.fccDataCenterContractTypeGroup',
				items: [
					{ value: ContractTypeGroupEnum.S, i18nPath: 'contract.form.contractTypeGroupItem.S' },
					{ value: ContractTypeGroupEnum.A, i18nPath: 'contract.form.contractTypeGroupItem.A' },
					{ value: ContractTypeGroupEnum.G, i18nPath: 'contract.form.contractTypeGroupItem.G' },
					{ value: ContractTypeGroupEnum.V, i18nPath: 'contract.form.contractTypeGroupItem.V' }
				]
			},
			{
				order: 5,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.STATUS,
				type: FilterTypes.FILTER_LIST_TYPE,
				i18nPath: 'shared.forms.status',
				values: [ ContractStatusEnum.ACTIVE ],
				items: [
					{ value: ContractStatusEnum.ACTIVE, i18nPath: 'shared.status.active' },
					{ value: ContractStatusEnum.DISABLED, i18nPath: 'shared.status.disabled' }
				]
			},
			{
				order: 6,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.DATA_CENTER_ENABLED,
				type: FilterTypes.FILTER_SWITCH_TYPE,
				i18nPath: 'contract.filter.fccDataCenterEnabled',
				values: [],
				items: []
			}
		]
	},
	i18n: {
		...CONTRACT_BASE_METADATA?.i18n,
		basepath: APP_CONFIG.fiskalContracts.i18nPath
	},
	form: {
		...CONTRACT_BASE_METADATA?.form,
		name: 'contract',
		baseData: {
			...CONTRACT_BASE_METADATA?.form?.baseData,
			contractTypeGroup: {
				hide: false,
				specialTreatments: null,
				order: 5,
				i18nPath: 'contract.form.contractTypeGroup',
				required: true,
				addSpaceToPrevious: true
			},
			contractedCloudErs: {
				hide: false,
				specialTreatments: null,
				order: 6,
				i18nPath: 'contract.form.contractedCloudErs',
				required: false
			},
			contractedTokenErs: {
				hide: false,
				specialTreatments: null,
				order: 7,
				i18nPath: 'contract.form.contractedTokenErs',
				required: false
			},
			fccDataCenterContractTypeGroup: {
				hide: false,
				specialTreatments: null,
				order: 9,
				i18nPath: 'contract.form.fccDataCenterContractTypeGroup',
				required: false
			},
			fccDataCenterContractedBusinessPremises: {
				hide: false,
				specialTreatments: null,
				order: 10,
				i18nPath: 'contract.form.fccDataCenterContractedBusinessPremises',
				required: false
			},
			fccDataCenterEnabled: {
				hide: false,
				specialTreatments: null,
				order: 11,
				i18nPath: 'contract.form.fccDataCenterEnabled',
				required: false
			},
			fccDataCenterVolume: {
				hide: false,
				specialTreatments: null,
				order: 12,
				i18nPath: 'contract.form.fccDataCenterVolume',
				required: false
			},
			contractedFccDataCenterErs: {
				hide: false,
				specialTreatments: null,
				order: 13,
				i18nPath: 'contract.form.contractedFccDataCenterErs',
				required: false
			},
			contractedBusinessPremises: {
				hide: false,
				specialTreatments: null,
				order: 14,
				i18nPath: 'contract.form.contractedBusinessPremises',
				required: true
			},
			businessPremisesLimit: {
				hide: false,
				specialTreatments: null,
				order: 15,
				i18nPath: 'contract.form.businessPremisesLimit',
				required: true
			},
			volume: {
				hide: false,
				specialTreatments: null,
				order: 16,
				i18nPath: 'contract.form.volume',
				required: true
			},
			notifications: {
				hide: false,
				specialTreatments: null,
				order: 17,
				i18nPath: 'shared.form.email',
				required: false
			}
		},
		preserveDataMyAccount: [
			...CONTRACT_BASE_METADATA?.form.preserveData,
			{ key: 'contractPropertiesLog.businessPremisesLimit', fallbackValue: null },
			{ key: 'contractPropertiesLog.contractTypeGroup', fallbackValue: null },
			{ key: 'contractPropertiesLog.contractedBusinessPremises', fallbackValue: null },
			{ key: 'contractPropertiesLog.contractedCloudErs', fallbackValue: null },
			{ key: 'contractPropertiesLog.contractedFccDataCenterErs', fallbackValue: null },
			{ key: 'contractPropertiesLog.fccDataCenterContractedBusinessPremises', fallbackValue: null },
			{ key: 'contractPropertiesLog.fccDataCenterVolume', fallbackValue: null },
			{ key: 'contractPropertiesLog.contractedTokenErs', fallbackValue: null },
			{ key: 'contractPropertiesLog.fccDataCenterEnabled', fallbackValue: null },
			{ key: 'contractPropertiesLog.from', fallbackValue: null },
			{ key: 'contractPropertiesLog.volume', fallbackValue: null },
			{ key: 'customerName', fallbackValue: null }
		]
	}
};
